import { useMemo } from 'react';
import {
  DEFAULT_QUERY_RESOLUTION,
  getSuggestedStepMs,
  isValidResolutionLevel,
  mapResolutionToMaxDatapoints,
  QueryResolution,
  resolutionLevelsSchema,
} from '@/core';
import { useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';
import { useVizFrameworkConfig } from '../../model';
import { AUTO_RESOLUTION_HIGH, AUTO_RESOLUTION_LOW, useStaticStepResolutionMs, useStepResolution } from './resolution';

type UseSuggestedStepMsArgs = {
  /**
   * Note: You most likely don't need to set this, only use for advanced use cases.
   * The maximum number of data points that should be used when calculating the step size.
   * The step size is calculated by time range / maxDatapoints to ensure even bucket sizes.
   * @default 300
   */
  maxDatapoints?: number;

  /**
   * Impacts max datapoints per series by providing presets of 'Low', 'Medium', or 'High'.
   * A custom resolution can also be set as a duration string (ex: '30s', '5m').
   * @default 'Medium'
   */
  resolution?: QueryResolution;

  /**
   * The factor to multiply the maxDatapoints by. This can be used to adjust the maxDatapoints based on the
   * resolution of the data. For example, if you want a higher resolution, you can increase this value.
   * @default 1
   */
  resolutionMultiplier?: number;
  /**
   * Whether the calculation should be enabled. If false, the step will be 0.
   * @default true
   */
  enabled?: boolean;
};

/**
 * Gets the suggested step size in ms using the current time range in context.
 * This is calculated by dividing the time range in ms by the maxDatapoints.
 * Note: You generally don't need to call this directly unless you need to do custom calculations.
 */
export function useSuggestedStepMs(args?: UseSuggestedStepMsArgs): number {
  const { resolution, resolutionMultiplier = 1, enabled = true } = args || {};
  const { absoluteTimeRange } = useTimeRange();
  const config = useVizFrameworkConfig();
  const stepResolution = useStepResolution();
  const staticResolutionMs = useStaticStepResolutionMs(resolution);

  const suggestedStepMs = useMemo(() => {
    // Enabled is used so step is not calculated unnecessarily (ex: while chart is not in the viewport).
    if (!enabled) return 0;

    // Return the static resolution if it exists, includes custom resolution set in query_options
    if (staticResolutionMs) {
      return staticResolutionMs;
    }

    let resolutionLevel = isValidResolutionLevel(resolution) ? resolution : DEFAULT_QUERY_RESOLUTION;

    // Ensures resolution passed as prop takes precedence over resolution from context
    if (resolution === undefined) {
      if (stepResolution === AUTO_RESOLUTION_HIGH) {
        resolutionLevel = resolutionLevelsSchema.enum.High;
      } else if (stepResolution === AUTO_RESOLUTION_LOW) {
        resolutionLevel = resolutionLevelsSchema.enum.Low;
      }
    }

    // Use resolution to determine maxDatapoints, account for feature flags
    const inputMaxDatapoints = config?.maxDatapointsPerSeries;
    let maxDatapoints = mapResolutionToMaxDatapoints(resolutionLevel, inputMaxDatapoints);
    if (args?.maxDatapoints) {
      maxDatapoints = args.maxDatapoints;
    }
    const modifiedMaxDataPoints = maxDatapoints * resolutionMultiplier;
    return getSuggestedStepMs(absoluteTimeRange, modifiedMaxDataPoints);
  }, [
    resolution,
    stepResolution,
    resolutionMultiplier,
    enabled,
    args?.maxDatapoints,
    config?.maxDatapointsPerSeries,
    absoluteTimeRange,
    staticResolutionMs,
  ]);

  return suggestedStepMs;
}
